/**
 * Welds one solid's planar boundary faces into a STEP manifold B-rep:
 * vertices dedupe by coordinate key, and each undirected edge becomes one
 * EDGE_CURVE used by its two adjacent faces in opposite senses, which is what
 * makes importers accept a closed manifold.
 */

import { normalize, type Vec3 } from '../math3d';
import { newellNormal, type PlanarFace, type SpacePoint } from './planarFace';
import { stepString, type StepWriter } from './stepWriter';

interface StepVertex {
  reference: string;
  point: Vec3;
}

/** One solid named `name` from its boundary faces, with export space mapped to the file's frame by `toFile`; returns its MANIFOLD_SOLID_BREP. */
export function stepSolid(writer: StepWriter, toFile: (point: SpacePoint) => Vec3, name: string, faces: PlanarFace[]): string {
  const { entity, direction, cartesianPoint } = writer;
  const vertexReferences = new Map<string, StepVertex>();
  const vertex = (point: Vec3): StepVertex => {
    const key = point.map(value => value.toFixed(6)).join(',');
    let found = vertexReferences.get(key);
    if (!found) {
      found = { reference: entity(`VERTEX_POINT('',${cartesianPoint(point)})`), point };
      vertexReferences.set(key, found);
    }
    return found;
  };
  const edgeReferences = new Map<string, { reference: string; fromReference: string }>();
  const edge = (from: StepVertex, to: StepVertex): { reference: string; forward: boolean } => {
    const key = [from.reference, to.reference].sort().join('|');
    let found = edgeReferences.get(key);
    if (!found) {
      const delta = normalize([to.point[0] - from.point[0], to.point[1] - from.point[1], to.point[2] - from.point[2]]);
      const line = entity(`LINE('',${cartesianPoint(from.point)},${entity(`VECTOR('',${direction(delta)},1.0)`)})`);
      found = { reference: entity(`EDGE_CURVE('',${from.reference},${to.reference},${line},.T.)`), fromReference: from.reference };
      edgeReferences.set(key, found);
    }
    return { reference: found.reference, forward: found.fromReference === from.reference };
  };
  const loopBound = (ring: PlanarFace['rings'][number], map: PlanarFace['map'], outer: boolean): string | null => {
    const vertices = ring.map(point => vertex(toFile(map(point))));
    const orientedEdges: string[] = [];
    for (let i = 0; i < vertices.length; i++) {
      const from = vertices[i], to = vertices[(i + 1) % vertices.length];
      if (from.reference === to.reference) continue;
      const { reference, forward } = edge(from, to);
      orientedEdges.push(entity(`ORIENTED_EDGE('',*,*,${reference},${forward ? '.T.' : '.F.'})`));
    }
    if (orientedEdges.length < 3) return null;
    const loop = entity(`EDGE_LOOP('',(${orientedEdges.join(',')}))`);
    return entity(`${outer ? 'FACE_OUTER_BOUND' : 'FACE_BOUND'}('',${loop},.T.)`);
  };

  const faceReferences: string[] = [];
  for (const { rings, map } of faces) {
    const bounds = rings.map((ring, ringIndex) => loopBound(ring, map, ringIndex === 0)).filter((bound): bound is string => bound !== null);
    if (!bounds.length) continue;
    faceReferences.push(entity(`ADVANCED_FACE('',(${bounds.join(',')}),${facePlane(writer, rings[0].map(point => toFile(map(point))))},.T.)`));
  }
  return entity(`MANIFOLD_SOLID_BREP('${stepString(name)}',${entity(`CLOSED_SHELL('',(${faceReferences.join(',')}))`)})`);
}

/**
 * A face's PLANE: through its outer ring's first point, normal to the ring's
 * Newell normal (the face's true geometric normal, which the face contract
 * keeps outward; the outward hint itself can be oblique, a wall's mitered
 * corner direction say, and would skew the surface).
 */
function facePlane({ entity, direction, cartesianPoint }: StepWriter, outer: Vec3[]): string {
  const normal = newellNormal(outer.map(([x, y, h]) => ({ x, y, h })));
  const reference = normalize([outer[1][0] - outer[0][0], outer[1][1] - outer[0][1], outer[1][2] - outer[0][2]]);
  return entity(
    `PLANE('',${entity(`AXIS2_PLACEMENT_3D('',${cartesianPoint(outer[0])},${direction(normalize([normal.x, normal.y, normal.h]))},${direction(reference)})`)})`,
  );
}
